import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { RFQ } from '../types';
import { useRFQ } from '../contexts/RFQContext';
import { useBomStatusPoll } from './useBomStatusPoll';
import { bomClient } from '../client/bomClient';
import { useOnRobbieApplied } from '../contexts/RobbieUiContext';

/**
 * The BOM behind step 3 and its robotics analysis run: which BOM is active,
 * whether the analysis is running or failed, the analysis result, and the
 * components with live research state merged in from the status poll (that
 * state is never stored on the BOM, so the RFQ components do not carry it).
 */
export const useBomAnalysisRun = (rfq: RFQ) => {
  const rfqData = useSelector((state: RootState) => state.rfq.rfqData);
  const { fetchRFQ } = useRFQ();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  // The analysis run itself failed (not research): the step offers to run it again.
  const [analysisFailed, setAnalysisFailed] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- the analysis payload is untyped server data
  const [analysisData, setAnalysisData] = useState<any>(null);
  // The server's start time for the running analysis, so the wait's range is
  // counted from when it really began — not from this page load.
  const [analysisStartedAt, setAnalysisStartedAt] = useState<string | null>(null);

  const poll = useBomStatusPoll({
    rfqId: rfq?.rfqId,
    refreshRfq: (id) => fetchRFQ(id, true, true),
    onAnalysis: (analysis) => {
      setAnalysisData(analysis);
      setIsAnalyzing(false);
    },
    onAnalysisFailed: () => {
      setAnalysisFailed(true);
      setIsAnalyzing(false);
    },
    onAnalysisRunning: setAnalysisStartedAt,
  });
  const { liveResearch, research, start: startPolling } = poll;

  const bomComponents = rfqData?.boms?.[0]?.components;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- extractedData is not on the RFQ type
  const extractedComponents = (rfqData as any)?.extractedData?.components;
  const components = useMemo(() => {
    const base = bomComponents || extractedComponents || [];
    return base.map((c: { _id?: unknown; id?: unknown }) => {
      const live = liveResearch[String(c?._id)] ?? liveResearch[String(c?.id)];
      return live ? { ...c, ...live } : c;
    });
  }, [bomComponents, extractedComponents, liveResearch]);

  const latestBom = rfqData?.boms?.[0];
  const latestBomId: string | undefined = latestBom?.bomId;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- older RFQ payloads carry boms on the prop
  const activeBomIdRef: MutableRefObject<string | null> = useRef(latestBomId || (rfq as any)?.boms?.[0]?.bomId || null);

  // Start the poll once per BOM; it fetches the analysis and research state.
  useEffect(() => {
    if (!latestBom?.bomId) return;
    activeBomIdRef.current = latestBom.bomId;
    startPolling(latestBom.bomId);
    if (latestBom.status !== 'completed' && latestBom.status !== 'failed') setIsAnalyzing(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the BOM id only
  }, [latestBomId]);

  // A Robbie validation card the buyer applied wrote verdicts (and maybe
  // fixes); re-read the analysis so the findings show them at once.
  useOnRobbieApplied('validation_actions', (event) => {
    bomClient.getAnalysisStatus(event.bomId).then(setAnalysisData).catch(() => undefined);
  });

  const restartAnalysis = useCallback(() => {
    setAnalysisFailed(false);
    setAnalysisStartedAt(null);
    setIsAnalyzing(true);
    if (activeBomIdRef.current) startPolling(activeBomIdRef.current);
  }, [startPolling]);

  return {
    rfqData,
    bomId: latestBomId || activeBomIdRef.current,
    activeBomIdRef,
    components,
    research,
    analysisData,
    setAnalysisData,
    isAnalyzing,
    analysisStartedAt,
    analysisFailed,
    startPolling,
    restartAnalysis,
    refreshRfq: () => (rfq?.rfqId ? fetchRFQ(rfq.rfqId, true, true) : Promise.resolve()),
  };
};

export type BomAnalysisRun = ReturnType<typeof useBomAnalysisRun>;
